import { useState, useEffect, useMemo } from 'react';
import { statsApi, type DashboardStats } from '../../api/statsApi';
import { bookingApi, type BookingResponse, type BookingStatus } from '../../api/bookingApi';
import { employeeApi, type EmployeeResponse } from '../../api/employeeApi';
import { DollarSign, Calendar, Users, TrendingUp, Clock, Phone, AlertTriangle, Search, Filter, RefreshCw, ChevronRight, Shield } from 'lucide-react';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import { authApi, type AuthResponse } from '../../api/authApi';
import SalonSelector from './SalonSelector';
import { useTutorial } from '../../hooks/useTutorial';

const DashboardPage = () => {
    const [stats, setStats] = useState<DashboardStats | null>(null);
    const [todayAppointments, setTodayAppointments] = useState<BookingResponse[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingAppointments, setIsLoadingAppointments] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
    const [updatingAppointmentId, setUpdatingAppointmentId] = useState<string | null>(null);

    // Search/Filter state
    const [searchQuery, setSearchQuery] = useState('');
    const [filterType, setFilterType] = useState<'ALL' | 'CLIENT' | 'EMPLOYEE'>('ALL');
    // @ts-ignore - Variable is set but not directly read, used for future feature
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [employees, setEmployees] = useState<EmployeeResponse[]>([]);

    // User and Salon state
    const [currentUser, setCurrentUser] = useState<AuthResponse | null>(null);
    const [activeSalonId, setActiveSalonId] = useState<string | null>(null);
    const isSuperAdmin = currentUser?.role === 'SUPER_ADMIN';

    // Tutorial hook
    const { startTutorial, isTutorialCompleted } = useTutorial();

    // Load user on mount
    useEffect(() => {
        const userStr = localStorage.getItem('user');
        if (userStr) {
            const user: AuthResponse = JSON.parse(userStr);
            setCurrentUser(user);
            if (user.salonId) {
                setActiveSalonId(user.salonId);
            }
        }
        setIsLoading(false);
        setIsLoadingAppointments(false);
    }, []);

    // Auto-start tutorial
    useEffect(() => {
        if (currentUser && activeSalonId && !isTutorialCompleted()) {
            const timer = setTimeout(() => {
                startTutorial();
            }, 2500);
            return () => clearTimeout(timer);
        }
    }, [currentUser, activeSalonId, isTutorialCompleted, startTutorial]);


    // Load employees
    useEffect(() => {
        if (!activeSalonId) return;
        const loadEmployees = async () => {
            try {
                const employeesData = await employeeApi.getSalonEmployees(activeSalonId);
                if (Array.isArray(employeesData)) {
                    setEmployees(employeesData.filter(emp => emp.isActive));
                } else {
                    setEmployees([]);
                }
            } catch (error) {
                console.error('Error loading employees:', error);
                setEmployees([]);
            }
        };
        loadEmployees();
    }, [activeSalonId]);

    // Data loading logic
    const loadDashboardData = async (silent: boolean = false) => {
        if (!activeSalonId) return;

        if (!silent) {
            setIsLoading(true);
            setIsLoadingAppointments(true);
        }
        setError(null);

        try {
            const today = format(new Date(), 'yyyy-MM-dd');
            const [statsData, appointmentsData] = await Promise.all([
                statsApi.getDashboardStats(activeSalonId),
                bookingApi.getSalonAppointments(activeSalonId, today, today)
            ]);

            setStats(statsData);
            if (Array.isArray(appointmentsData)) {
                const allAppointments = appointmentsData
                    .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
                setTodayAppointments(allAppointments);
            } else {
                setTodayAppointments([]);
            }
            setLastUpdated(new Date());
        } catch (err) {
            console.error('Error loading dashboard data:', err);
            if (!silent) setError('Error al cargar los datos del dashboard');
            setTodayAppointments([]);
        } finally {
            setIsLoading(false);
            setIsLoadingAppointments(false);
        }
    };

    const handleRefresh = async () => {
        setIsLoading(true);
        try {
            // Refresh user data (for verification status)
            const updatedUser = await authApi.getMe();
            setCurrentUser(updatedUser);
            localStorage.setItem('user', JSON.stringify(updatedUser));

            // Refresh dashboard data
            await loadDashboardData(true);
        } catch (error) {
            console.error('Error refreshing:', error);
        } finally {
            setIsLoading(false);
        }
    };

    // Auto-refresh interval
    useEffect(() => {
        if (!activeSalonId) return;
        loadDashboardData(false);
        const refreshInterval = setInterval(() => {
            loadDashboardData(true);
        }, 30000);
        return () => clearInterval(refreshInterval);
    }, [activeSalonId]);

    // --- EMPLOYEE PORTAL LOGIC ---
    // Identify if current user is a stylist and find their employee ID
    const isStylist = currentUser?.role === 'STYLIST';
    const currentStylistId = useMemo(() => {
        if (!isStylist || !currentUser?.email || employees.length === 0) return null;
        return employees.find(e => e.email === currentUser.email)?.id;
    }, [isStylist, currentUser, employees]);

    // Filtering logic
    const filteredAppointments = useMemo(() => {
        return todayAppointments.filter(apt => {
            // Role-based filter: Stylists only see their own appointments
            if (isStylist && currentStylistId && apt.employeeId !== currentStylistId) {
                return false;
            }

            if (!searchQuery.trim()) return true;
            const query = searchQuery.toLowerCase();
            let matches = false;

            if (filterType === 'ALL' || filterType === 'CLIENT') {
                if (apt.clientName?.toLowerCase().includes(query)) matches = true;
                if (apt.clientPhone?.toLowerCase().includes(query)) matches = true;
            }

            if (!matches && (filterType === 'ALL' || filterType === 'EMPLOYEE')) {
                // If stylist, no need to search by employee name (it's them)
                if (!isStylist) {
                    const employee = employees.find(e => e.id === apt.employeeId);
                    if (employee) {
                        const employeeName = `${employee.firstName} ${employee.lastName}`.toLowerCase();
                        if (employeeName.includes(query)) matches = true;
                    }
                }
            }
            return matches;
        });
    }, [todayAppointments, searchQuery, employees, filterType, isStylist, currentStylistId]);

    // Helpers
    const formatCurrency = (value: number | undefined | null) => {
        if (value == null || isNaN(value)) return '$0';
        return new Intl.NumberFormat('es-CO', {
            style: 'currency',
            currency: 'COP',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(value);
    };

    const formatPercentage = (value: number | undefined | null) => {
        if (value == null || isNaN(value)) return '0%';
        return `${value.toFixed(1)}%`;
    };

    const formatTime = (dateString: string) => {
        return format(new Date(dateString), 'h:mm a');
    };

    // Grouping
    const groupAppointmentsByHour = (appointments: BookingResponse[]) => {
        const grouped = new Map<string, BookingResponse[]>();
        appointments.forEach(apt => {
            const timeKey = format(new Date(apt.startTime), 'h:mm a');
            if (!grouped.has(timeKey)) grouped.set(timeKey, []);
            grouped.get(timeKey)!.push(apt);
        });
        return Array.from(grouped.entries());
    };

    // Attention needed logic
    const appointmentsNeedingAttention = useMemo(() => {
        const now = new Date();
        return todayAppointments.filter(apt => {
            // Filter for stylist
            if (isStylist && currentStylistId && apt.employeeId !== currentStylistId) return false;

            const endTime = new Date(apt.endTime);
            return endTime < now && (apt.status === 'SCHEDULED' || apt.status === 'CONFIRMED');
        });
    }, [todayAppointments, isStylist, currentStylistId]);

    const handleStatusChange = async (appointmentId: string, newStatus: BookingStatus) => {
        try {
            setUpdatingAppointmentId(appointmentId);
            await bookingApi.updateAppointment(appointmentId, { status: newStatus });
            await loadDashboardData();
        } catch (error) {
            console.error('Error updating status:', error);
            setError('Failed to update status');
        } finally {
            setUpdatingAppointmentId(null);
        }
    };

    // --- RENDER ---

    // Empty State
    if (!activeSalonId && !isSuperAdmin) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh] space-y-8 animate-in fade-in duration-500">
                <div className="relative">
                    <div className="absolute inset-0 bg-emerald-500 blur-3xl opacity-20 rounded-full"></div>
                    <div className="relative bg-white/80 backdrop-blur-xl p-8 rounded-3xl border border-white/20 shadow-2xl">
                        <div className="bg-gradient-to-br from-emerald-100 to-teal-50 p-6 rounded-2xl">
                            <Calendar className="w-16 h-16 text-emerald-600" />
                        </div>
                    </div>
                </div>
                <div className="text-center max-w-md space-y-4">
                    <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                        ¡Bienvenido a Cresta! 🎉
                    </h1>
                    <p className="text-gray-500 text-lg leading-relaxed">
                        Parece que aún no tienes un salón configurado. Termina de configurar tu cuenta para empezar a gestionar citas.
                    </p>
                    <a href="/app/onboarding" className="inline-flex items-center gap-2 px-8 py-4 bg-gray-900 text-white font-semibold rounded-xl hover:bg-gray-800 transition-all shadow-lg hover:shadow-xl hover:-translate-y-1">
                        <Users className="w-5 h-5" />
                        Configurar mi Salón
                    </a>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-8 pb-10">
            {/* Header Section */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 animate-in slide-in-from-top-4 duration-500">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900 tracking-tight">
                        {isStylist ? 'Mi Agenda' : 'Dashboard'}
                    </h1>
                    <div className="flex items-center gap-2 mt-1">
                        <p className="text-gray-500 font-medium">
                            {isStylist ? 'Tus citas y actividades de hoy' : 'Resumen de actividad en tiempo real'}
                        </p>
                        {lastUpdated && (
                            <span className="text-xs px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full font-medium flex items-center gap-1 animate-pulse">
                                <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></div>
                                Live
                            </span>
                        )}
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    {isSuperAdmin && (
                        <SalonSelector currentSalonId={activeSalonId} onSalonChange={setActiveSalonId} />
                    )}
                    <button
                        onClick={handleRefresh}
                        disabled={isLoading}
                        className="group flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 rounded-xl hover:border-gray-300 hover:bg-gray-50 transition-all shadow-sm active:scale-95"
                    >
                        <RefreshCw className={`w-4 h-4 text-gray-500 group-hover:text-emerald-600 transition-colors ${isLoading ? 'animate-spin text-emerald-600' : ''}`} />
                        <span className="text-sm font-medium text-gray-700">Actualizar</span>
                    </button>
                </div>
            </div>

            {error && (
                <div className="bg-red-50/50 backdrop-blur-sm border border-red-200 text-red-700 px-6 py-4 rounded-2xl flex items-center gap-3 shadow-sm animate-in fade-in slide-in-from-top-2">
                    <AlertTriangle className="w-5 h-5 text-red-500" />
                    <p className="font-medium">{error}</p>
                </div>
            )}



            {/* Attention Alert - Glassmorphic */}
            {appointmentsNeedingAttention.length > 0 && (
                <div className="relative overflow-hidden bg-amber-50/80 backdrop-blur-md border border-amber-200/50 rounded-2xl p-5 shadow-sm animate-in fade-in slide-in-from-bottom-4 duration-500">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-amber-400/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
                    <div className="flex items-start gap-4 relative z-10">
                        <div className="p-3 bg-white/60 rounded-xl shadow-sm border border-amber-100">
                            <AlertTriangle className="w-6 h-6 text-amber-500" />
                        </div>
                        <div className="flex-1">
                            <h4 className="text-lg font-bold text-gray-900">
                                {appointmentsNeedingAttention.length} citas requieren atención
                            </h4>
                            <p className="text-gray-600 mt-1 mb-3">
                                {isStylist ? 'Tienes citas pasadas sin actualizar estado.' : 'Estas citas han pasado su hora programada.'}
                            </p>
                            <div className="flex flex-wrap gap-2">
                                {appointmentsNeedingAttention.slice(0, 4).map(apt => (
                                    <span key={apt.id} className="inline-flex items-center gap-1.5 px-3 py-1 bg-white border border-amber-100 text-amber-800 rounded-full text-xs font-semibold shadow-sm">
                                        <Clock className="w-3 h-3" />
                                        {formatTime(apt.startTime)} - {apt.clientName}
                                    </span>
                                ))}
                                {appointmentsNeedingAttention.length > 4 && (
                                    <span className="text-xs text-amber-600 font-medium self-center">+{appointmentsNeedingAttention.length - 4} más</span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Quick Access: Booking Approvals - Only for Owner/Manager */}
            {!isStylist && (
                <Link
                    to="/app/booking-approvals"
                    className="group relative overflow-hidden bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200/50 rounded-2xl p-5 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-0.5 block"
                >
                    <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-400/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
                    <div className="flex items-center gap-4 relative z-10">
                        <div className="p-3 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                            <Shield className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex-1">
                            <h4 className="text-lg font-bold text-gray-900">Reservas Públicas</h4>
                            <p className="text-gray-500 text-sm">Aprueba o rechaza las reservas de tus clientes</p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-emerald-600 group-hover:translate-x-1 transition-all" />
                    </div>
                </Link>
            )}

            {/* Stats Grid - Show ONLY for Admin/Manager/Owner */}
            {!isStylist && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {[
                        {
                            title: "Ventas Hoy",
                            value: stats ? formatCurrency(stats.totalSalesToday) : '$0',
                            sub: "Total facturado",
                            icon: <DollarSign className="w-6 h-6 text-white" />,
                            gradient: "from-emerald-500 to-teal-500",
                            textGradient: "from-emerald-600 to-teal-600",
                            bg: "bg-emerald-50",
                            delay: "0ms"
                        },
                        {
                            title: "Citas Hoy",
                            value: stats?.appointmentsTodayCount ?? 0,
                            sub: "En agenda",
                            icon: <Calendar className="w-6 h-6 text-white" />,
                            gradient: "from-blue-500 to-indigo-500",
                            textGradient: "from-blue-600 to-indigo-600",
                            bg: "bg-blue-50",
                            delay: "100ms"
                        },
                        {
                            title: "Clientes",
                            value: stats?.activeClientsCount ?? 0,
                            sub: "Base de datos",
                            icon: <Users className="w-6 h-6 text-white" />,
                            gradient: "from-violet-500 to-fuchsia-500",
                            textGradient: "from-violet-600 to-fuchsia-600",
                            bg: "bg-violet-50",
                            delay: "200ms"
                        },
                        {
                            title: "Eficiencia",
                            value: stats ? formatPercentage(stats.staffEfficiency) : '0%',
                            sub: "Ocupación staff",
                            icon: <TrendingUp className="w-6 h-6 text-white" />,
                            gradient: "from-orange-400 to-amber-500",
                            textGradient: "from-orange-600 to-amber-600",
                            bg: "bg-orange-50",
                            delay: "300ms"
                        }
                    ].map((stat, idx) => (
                        <div
                            key={idx}
                            className="group relative bg-white rounded-3xl p-6 border border-gray-100 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] hover:shadow-[0_8px_30px_-4px_rgba(0,0,0,0.1)] transition-all duration-300 hover:-translate-y-1 overflow-hidden"
                            style={{ animationDelay: stat.delay, animationFillMode: 'both' }}
                        >
                            {/* Background Decoration */}
                            <div className={`absolute top-0 right-0 w-32 h-32 bg-gradient-to-br ${stat.gradient} opacity-[0.03] group-hover:opacity-[0.08] rounded-full blur-2xl -translate-y-1/2 translate-x-1/2 transition-opacity duration-300`}></div>

                            <div className="flex justify-between items-start mb-4 relative z-10">
                                <div>
                                    <p className="text-sm font-semibold text-gray-400 uppercase tracking-wide">{stat.title}</p>
                                </div>
                                <div className={`p-3 rounded-2xl bg-gradient-to-br ${stat.gradient} shadow-lg shadow-gray-200 group-hover:scale-110 transition-transform duration-300`}>
                                    {stat.icon}
                                </div>
                            </div>

                            {isLoading ? (
                                <div className="space-y-2 animate-pulse">
                                    <div className="h-8 bg-gray-100 rounded w-2/3"></div>
                                    <div className="h-4 bg-gray-50 rounded w-1/3"></div>
                                </div>
                            ) : (
                                <div className="relative z-10">
                                    <h3 className={`text-4xl font-bold bg-gradient-to-r ${stat.textGradient} bg-clip-text text-transparent`}>
                                        {stat.value}
                                    </h3>
                                    <p className="text-sm text-gray-400 font-medium mt-1 flex items-center gap-1">
                                        {stat.sub}
                                        <ChevronRight className="w-3 h-3 opacity-50" />
                                    </p>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Stylist Specific Stats (Simplified) */}
            {isStylist && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-in fade-in slide-in-from-bottom-4">
                    <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-3xl p-6 text-white shadow-lg shadow-blue-500/20">
                        <div className="flex items-center gap-4">
                            <div className="p-3 bg-white/20 rounded-2xl backdrop-blur-sm">
                                <Calendar className="w-8 h-8 text-white" />
                            </div>
                            <div>
                                <p className="text-blue-100 font-medium">Mis Citas de Hoy</p>
                                <h3 className="text-4xl font-bold">{filteredAppointments.length}</h3>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white rounded-3xl p-6 border border-gray-100 shadow-sm flex items-center gap-4">
                        <div className="p-3 bg-emerald-50 rounded-2xl">
                            <TrendingUp className="w-8 h-8 text-emerald-600" />
                        </div>
                        <div>
                            <p className="text-gray-500 font-medium">Estado</p>
                            <div className="flex gap-2 mt-1">
                                <span className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-bold">Cuenta Activa</span>
                                <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-bold">Estilista</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Agenda/Schedule Section - Glassmorphic Container */}
            <div className="bg-white/90 backdrop-blur-md rounded-3xl border border-gray-200/50 shadow-sm overflow-hidden animate-in fade-in slide-in-from-bottom-8 duration-700 delay-300">
                {/* Header with Search */}
                <div className="p-6 border-b border-gray-100 flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h2 className="text-xl font-bold text-gray-900">Agenda de Hoy</h2>
                        <p className="text-sm text-gray-500 mt-0.5">
                            {filteredAppointments.length} citas encontradas
                        </p>
                    </div>

                    <div className="flex gap-2 w-full md:w-auto">
                        <div className="relative flex-1 md:w-64">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => {
                                    setSearchQuery(e.target.value);
                                    setShowSuggestions(true);
                                }}
                                onFocus={() => setShowSuggestions(true)}
                                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                                placeholder={isStylist ? "Buscar cliente..." : "Buscar cliente o empleado..."}
                                className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none transition-all"
                            />
                            {/* Suggestions would go here */}
                        </div>
                        {/* Only show employee filter if NOT a stylist */}
                        {!isStylist && (
                            <div className="relative">
                                <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                                    <Filter className="w-4 h-4 text-gray-400" />
                                </div>
                                <select
                                    value={filterType}
                                    onChange={(e) => setFilterType(e.target.value as any)}
                                    className="pl-9 pr-8 py-2 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-600 focus:ring-2 focus:ring-emerald-500/20 outline-none appearance-none cursor-pointer hover:bg-gray-50 transition-colors"
                                >
                                    <option value="ALL">Todos</option>
                                    <option value="CLIENT">Clientes</option>
                                    <option value="EMPLOYEE">Empleados</option>
                                </select>
                            </div>
                        )}
                    </div>
                </div>

                {/* Content */}
                <div className="p-6 min-h-[400px]">
                    {isLoadingAppointments ? (
                        <div className="space-y-4">
                            {[1, 2, 3].map(i => (
                                <div key={i} className="flex gap-4 p-4 rounded-2xl bg-gray-50 animate-pulse">
                                    <div className="w-16 h-16 bg-gray-200 rounded-xl" />
                                    <div className="flex-1 space-y-2 py-1">
                                        <div className="h-4 bg-gray-200 rounded w-1/3" />
                                        <div className="h-3 bg-gray-200 rounded w-1/4" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : filteredAppointments.length === 0 ? (
                        <div className="h-full flex flex-col items-center justify-center text-center py-20 text-gray-400">
                            <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mb-4">
                                <Calendar className="w-8 h-8 opacity-50" />
                            </div>
                            <p className="text-lg font-medium text-gray-600">
                                {isStylist ? 'No tienes citas hoy' : 'Sin citas para mostrar'}
                            </p>
                            <p className="text-sm">
                                {isStylist ? '¡Disfruta tu tiempo libre!' : 'Intenta ajustar los filtros de búsqueda'}
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-8">
                            {groupAppointmentsByHour(filteredAppointments).map(([time, groupApts]) => (
                                <div key={time} className="relative pl-8 md:pl-0">
                                    {/* Timeline line for mobile */}
                                    <div className="absolute left-2.5 top-0 bottom-0 w-0.5 bg-gray-100 md:hidden"></div>

                                    <div className="flex flex-col md:flex-row md:gap-6">
                                        {/* Time Column */}
                                        <div className="md:w-24 flex-shrink-0 mb-4 md:mb-0 relative">
                                            <div className="inline-flex items-center justify-center px-3 py-1 bg-gray-100 text-gray-600 text-xs font-bold rounded-lg border border-gray-200">
                                                {time}
                                            </div>
                                            <div className="absolute left-[-1.35rem] top-3 w-3 h-3 bg-white border-2 border-emerald-500 rounded-full md:hidden z-10"></div>
                                        </div>

                                        {/* Cards Grid */}
                                        <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                                            {groupApts.map((apt) => (
                                                <div
                                                    key={apt.id}
                                                    className={`group relative bg-white border border-gray-200 rounded-2xl p-4 shadow-sm hover:shadow-md hover:border-emerald-200 transition-all duration-300 ${updatingAppointmentId === apt.id ? 'opacity-50 pointer-events-none' : ''}`}
                                                >
                                                    <div className="flex justify-between items-start mb-3">
                                                        <div className="flex items-center gap-3">
                                                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center text-gray-600 font-bold text-sm">
                                                                {apt.clientName?.substring(0, 2).toUpperCase()}
                                                            </div>
                                                            <div>
                                                                <h4 className="font-bold text-gray-900 text-sm leading-tight">
                                                                    {apt.clientName}
                                                                </h4>
                                                                <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
                                                                    <Phone className="w-3 h-3" />
                                                                    {apt.clientPhone || 'Sin teléfono'}
                                                                </p>
                                                            </div>
                                                        </div>
                                                        {/* Status Badge */}
                                                        <span className={`px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider border ${apt.status === 'CONFIRMED' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' :
                                                            apt.status === 'COMPLETED' ? 'bg-blue-50 text-blue-700 border-blue-100' :
                                                                apt.status === 'CANCELLED' ? 'bg-red-50 text-red-700 border-red-100' :
                                                                    'bg-gray-50 text-gray-600 border-gray-100'
                                                            }`}>
                                                            {apt.status === 'IN_PROGRESS' ? 'EN CURSO' : apt.status}
                                                        </span>
                                                    </div>

                                                    <div className="pl-[3.25rem]">
                                                        <div className="text-sm font-medium text-gray-800 mb-3 bg-gray-50 px-3 py-2 rounded-lg inline-block">
                                                            {apt.serviceName}
                                                        </div>

                                                        <div className="flex items-center justify-between pt-2 border-t border-gray-50">
                                                            <p className="text-xs text-gray-400">
                                                                Fin: {formatTime(apt.endTime)}
                                                            </p>

                                                            {/* Quick Actions (Hover) */}
                                                            <select
                                                                value={apt.status}
                                                                onChange={(e) => handleStatusChange(apt.id, e.target.value as BookingStatus)}
                                                                className="text-xs bg-transparent font-medium text-emerald-600 hover:text-emerald-700 cursor-pointer outline-none text-right appearance-none"
                                                                disabled={!!updatingAppointmentId}
                                                            >
                                                                <option value="SCHEDULED">➡️ Agendada</option>
                                                                <option value="CONFIRMED">✅ Confirmar</option>
                                                                <option value="IN_PROGRESS">🚀 Iniciar</option>
                                                                <option value="COMPLETED">🏁 Completar</option>
                                                                <option value="CANCELLED">❌ Cancelar</option>
                                                                <option value="NO_SHOW">👻 No Asistió</option>
                                                            </select>
                                                        </div>
                                                    </div>

                                                    {/* Updating overlay */}
                                                    {updatingAppointmentId === apt.id && (
                                                        <div className="absolute inset-0 bg-white/50 backdrop-blur-[1px] rounded-2xl flex items-center justify-center">
                                                            <div className="animate-spin rounded-full h-6 w-6 border-2 border-emerald-500 border-t-transparent"></div>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DashboardPage;
